import type { 
  BaseEvent,
  EventItem,
  Place,
  Show,
  AirtableEvent,
  TicketmasterEvent,
  TranslationContext
} from '../../types/shared/event';
import type { SupportedLanguages } from '../../i18n';
import { logger } from '../logger';

// Interface de base pour les adaptateurs d'événements
export interface EventAdapter<T, S = any> {
  formatEvent: (rawEvent: T) => EventItem;
  formatPlace: (rawPlace: S) => Place;
  translateDescription: (
    description: string,
    targetLang: SupportedLanguages,
    context: TranslationContext
  ) => Promise<string>;
  generateShowId: (event: EventItem) => string;
  getFavoriteId: (event: EventItem) => string;
  syncFavorite?: (event: EventItem, isFavorite: boolean) => Promise<void>;
  getPlaces: (events: EventItem[]) => Place[];
}

// Classe de base pour les adaptateurs
abstract class BaseEventAdapter<T, S = any> implements EventAdapter<T, S> {
  protected abstract source: 'airtable' | 'ticketmaster';

  protected validateEvent(event: Partial<BaseEvent>): boolean {
    const requiredFields = [
      'name',
      'description',
      'category',
      'date',
      'place',
      'latitude',
      'longitude',
      'distance'
    ];

    const missingFields = requiredFields.filter(field => {
      const value = event[field as keyof BaseEvent];
      return value === undefined || value === null || value === '';
    });

    if (missingFields.length > 0) {
      logger.warn('Missing required event fields', {
        category: 'Validation',
        data: { missingFields }
      });
      return false;
    }

    return true;
  }

  protected formatBaseEvent(rawEvent: any): Partial<BaseEvent> {
    return {
      name: rawEvent.name || '',
      descriptions: {},
      description: rawEvent.description || '',
      offer: rawEvent.offer || '',
      offers: {},
      fulladress: rawEvent.fulladress || '',
      telephone: rawEvent.telephone || '',
      url: rawEvent.url || '',
      itinerary: rawEvent.itinerary || '',
      category: rawEvent.category || 'Autre',
      type: rawEvent.type || '',
      image: rawEvent.image || '',
      latitude: Number(rawEvent.latitude) || 0,
      longitude: Number(rawEvent.longitude) || 0,
      distance: Number(rawEvent.distance) || 0,
      booking: rawEvent.booking || '',
      price: rawEvent.price || '',
      artiste: rawEvent.artiste || '',
      place: rawEvent.place || '',
      date: rawEvent.date || '',
      hour: rawEvent.hour || '00:00',
      source: this.source
    };
  }

  public getPlaces(events: EventItem[]): Place[] {
    try {
      const placeMap = new Map<string, Place>();

      events.forEach(event => {
        if (!placeMap.has(event.place)) {
          placeMap.set(event.place, {
            name: event.place,
            latitude: event.latitude,
            longitude: event.longitude,
            distance: event.distance,
            fulladress: event.fulladress,
            itinerary: event.itinerary,
            shows: []
          });
        }

        const place = placeMap.get(event.place)!;
        let show = place.shows.find(s => {
          if (event.source === 'airtable' && s.source === 'airtable') {
            return (event as AirtableEvent).idShow === (s as any).idShow;
          }
          if (event.source === 'ticketmaster' && s.source === 'ticketmaster') {
            return (event as TicketmasterEvent).showId === (s as any).showId;
          }
          return false;
        });

        if (!show) {
          show = {
            id: event.id,
            source: event.source,
            name: event.name,
            image: event.image,
            type: event.type,
            ...(event.source === 'airtable' 
              ? { idShow: (event as AirtableEvent).idShow }
              : { showId: (event as TicketmasterEvent).showId }
            ),
            events: []
          };
          place.shows.push(show);
        }

        show.events.push(event);
      });

      return Array.from(placeMap.values()).map(place => ({
        ...place,
        shows: place.shows.sort((a, b) => {
          const aDate = a.events[0].date;
          const bDate = b.events[0].date;
          const dateCompare = aDate.localeCompare(bDate);
          if (dateCompare !== 0) return dateCompare;
          return a.events[0].hour.localeCompare(b.events[0].hour);
        })
      }));

    } catch (error) {
      logger.error('Error creating places', {
        category: 'Adapter',
        data: { error, source: this.source }
      });
      return [];
    }
  }

  abstract formatEvent(rawEvent: T): EventItem;
  abstract formatPlace(rawPlace: S): Place;
  abstract translateDescription(
    description: string,
    targetLang: SupportedLanguages,
    context: TranslationContext
  ): Promise<string>;
  abstract generateShowId(event: EventItem): string;
  abstract getFavoriteId(event: EventItem): string;
}

// Utilitaires pour la gestion des spectacles
export const showUtils = {
  generateShowId(event: EventItem): string {
    const key = `${event.name}|${event.description}|${event.place}`;
    return `show_${btoa(key).replace(/[^a-zA-Z0-9]/g, '')}`;
  },

  isSameShow(event1: EventItem, event2: EventItem): boolean {
    if (event1.source !== event2.source) return false;

    if (event1.source === 'airtable' && event2.source === 'airtable') {
      return (event1 as AirtableEvent).idShow === (event2 as AirtableEvent).idShow;
    }

    if (event1.source === 'ticketmaster' && event2.source === 'ticketmaster') {
      return (event1 as TicketmasterEvent).showId === (event2 as TicketmasterEvent).showId;
    }

    return false;
  }
};

// Export unique de la classe de base
export { BaseEventAdapter };